import { getConfig } from '../../config'
import { type TFunction } from 'i18next'

export interface FileLimits {
  maxFreeFileSize: number
  maxPaidFileSize: number
  maxTotalProcessSize: number
  maxConcurrentFiles: number
  maxFreeSignaturesPerProcess: number
}

export interface ValidationContext {
  signatureType: 'freeSignature' | 'paidSignature'
  currentDocuments: Array<{ size: number; code?: string; name?: string }>
  t: TFunction
}

/**
 * Get file limits from configuration
 */
export function getFileLimits(): FileLimits {
  const config = getConfig()
  return {
    maxFreeFileSize: config.limits.maxFreeFileSize,
    maxPaidFileSize: config.limits.maxPaidFileSize,
    maxTotalProcessSize: config.limits.maxTotalProcessSize,
    maxConcurrentFiles: config.limits.maxConcurrentFiles,
    maxFreeSignaturesPerProcess: config.limits.maxFreeSignaturesPerProcess,
  }
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${Math.round(bytes / (1024 * 1024))}MB`
  }
  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)}KB`
  }
  return `${bytes}B`
}

/**
 * Validate that file name is not a duplicate
 */
export function validateDuplicateFileName(
  file: File,
  context: ValidationContext
): string | null {
  const existingFileNames = context.currentDocuments
    .map(doc => doc.name)
    .filter(Boolean) // Remove undefined names

  if (existingFileNames.includes(file.name)) {
    return context.t('documentAdd.errors.duplicateFileName')
  }

  return null
}

/**
 * Validate file size based on signature type and current context
 */
export function validateFileSize(
  file: File,
  context: ValidationContext
): string | null {
  const limits = getFileLimits()

  // Check individual file size limit based on signature type
  const maxIndividualSize =
    context.signatureType === 'freeSignature'
      ? limits.maxFreeFileSize
      : limits.maxPaidFileSize

  if (file.size > maxIndividualSize) {
    const maxSizeFormatted = formatFileSize(maxIndividualSize)
    return context.signatureType === 'freeSignature'
      ? context.t('documentAdd.errors.fileTooLargeFree', {
          size: maxSizeFormatted,
        })
      : context.t('documentAdd.errors.fileTooLargePaid', {
          size: maxSizeFormatted,
        })
  }

  // Check total process size limit
  const currentTotalSize = context.currentDocuments.reduce(
    (total, doc) => total + doc.size,
    0
  )
  const newTotalSize = currentTotalSize + file.size

  if (newTotalSize > limits.maxTotalProcessSize) {
    const remainingSpace = limits.maxTotalProcessSize - currentTotalSize
    const remainingFormatted = formatFileSize(remainingSpace)
    const maxTotalFormatted = formatFileSize(limits.maxTotalProcessSize)

    return context.t('documentAdd.errors.totalSizeExceeded', {
      remaining: remainingFormatted,
      maxTotal: maxTotalFormatted,
    })
  }

  return null
}

/**
 * Validate maximum number of concurrent files
 */
export function validateConcurrentFiles(
  currentDocumentCount: number,
  context: Pick<ValidationContext, 't'>
): string | null {
  const limits = getFileLimits()

  if (currentDocumentCount >= limits.maxConcurrentFiles) {
    return context.t('documentAdd.errors.tooManyFiles', {
      maxFiles: limits.maxConcurrentFiles,
    })
  }

  return null
}

/**
 * Validate maximum number of free signatures in process
 */
export function validateFreeSignatureLimit(
  context: ValidationContext
): string | null {
  if (context.signatureType !== 'freeSignature') {
    return null
  }

  const limits = getFileLimits()
  const currentFreeSignatures = context.currentDocuments.filter(
    doc => !doc.code
  ).length

  if (currentFreeSignatures >= limits.maxFreeSignaturesPerProcess) {
    return context.t('documentAdd.errors.tooManyFreeSignatures', {
      maxFree: limits.maxFreeSignaturesPerProcess,
    })
  }

  return null
}

/**
 * Comprehensive file validation
 */
export function validateFileUpload(
  file: File,
  context: ValidationContext
): string | null {
  // Check duplicate file name first
  const duplicateError = validateDuplicateFileName(file, context)
  if (duplicateError) return duplicateError

  // Check concurrent files limit
  const concurrentError = validateConcurrentFiles(
    context.currentDocuments.length,
    context
  )
  if (concurrentError) return concurrentError

  // Check free signature limit
  const freeSignatureError = validateFreeSignatureLimit(context)
  if (freeSignatureError) return freeSignatureError

  // Check file size
  const sizeError = validateFileSize(file, context)
  if (sizeError) return sizeError

  return null
}

/**
 * Check if free signatures are available based on configuration limits
 */
export function isFreeSignatureAvailable(
  currentDocuments: Array<{ code?: string }>
): boolean {
  const limits = getFileLimits()
  const currentFreeSignatures = currentDocuments.filter(doc => !doc.code).length
  return currentFreeSignatures < limits.maxFreeSignaturesPerProcess
}

/**
 * Check if adding more documents is possible
 */
export function canAddMoreDocuments(
  currentDocuments: Array<{ code?: string }>
): boolean {
  const limits = getFileLimits()
  return currentDocuments.length < limits.maxConcurrentFiles
}

/**
 * Get reason why adding documents is disabled
 */
export function getAddDocumentDisabledReason(
  currentDocuments: Array<{ code?: string }>,
  t: TFunction
): string | null {
  const limits = getFileLimits()

  if (currentDocuments.length >= limits.maxConcurrentFiles) {
    return t('documentAdd.errors.tooManyFiles', {
      maxFiles: limits.maxConcurrentFiles,
    })
  }

  return null
}
